import { useEffect, useRef, useState } from 'react';

import { Menu, MenuItem } from '../components/Menu.js';
import { Icon } from '../components/Icon.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './EndChatActions.module.css';

interface EndChatActionsProps {
  onEnd: () => void;
  /** Open the counterpart details side panel. */
  onDetails: () => void;
  disabled?: boolean;
  /**
   * Both acts are carried out on the full page from here, not in place — true
   * in the widget, which hands the chat to /chat for either. Marks them with the
   * arrow that says so, the primary included: unlike the hiring group, nothing
   * this one offers stays behind.
   */
  opensPage?: boolean;
  /** One size down, for the widget header. See `.groupCompact`. */
  compact?: boolean;
  /**
   * The menu opened or closed. Only the widget listens: its panel dismisses on
   * Escape, which would otherwise take the whole widget down with the menu.
   */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Split button ("button group") for a non-job (direct) conversation, matching
 * the hiring split button's weight so the two read as one control family. The
 * primary ends the chat — an outline that turns destructive-red only on hover.
 * The "…" menu carries Details; it is the direct chat's only secondary action
 * today, but the group is the seam future ones slot into.
 */
export function EndChatActions({
  onEnd,
  onDetails,
  disabled,
  opensPage,
  compact,
  onOpenChange,
}: EndChatActionsProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), rootRef);

  // Reported from the state, and reported closed on the way out, so a menu that
  // unmounts open never leaves the host believing it is still up.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);
  useEffect(() => () => onOpenChangeRef.current?.(false), []);

  // Sized with the button, like the hiring group's — see the note there.
  const glyph = compact ? 14 : 15;

  return (
    <div
      className={compact ? `${styles.group} ${styles.groupCompact}` : styles.group}
      ref={rootRef}
    >
      <button type="button" className={styles.primary} disabled={disabled} onClick={onEnd}>
        <Icon name="chatOff" size={glyph} />
        <span>End chat</span>
        {opensPage && (
          <span className={styles.opens}>
            <Icon name="arrowUpRight" size={compact ? 12 : 14} />
            {/* The arrow's spoken half — see the note in Menu.tsx. */}
            <span className={styles.srOnly}>Opens the full chat page</span>
          </span>
        )}
      </button>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More chat actions"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" size={compact ? 16 : 18} />
      </button>

      {open && (
        <Menu onClose={() => setOpen(false)}>
          <MenuItem icon="info" opensPage={opensPage} onSelect={onDetails}>
            Details
          </MenuItem>
        </Menu>
      )}
    </div>
  );
}
